/* País (o idioma) de un canal, una película o una serie, leído del prefijo que le pone el
   proveedor: «FR| Título», «ES - DEPORTES», «[UK] Canal», «|DE| …». Es una costumbre de las listas
   IPTV, no un dato fiable: solo se aceptan códigos conocidos, para no tomar por país un «HD -» o
   un «TV |». Si el nombre no lo trae, vale el de su categoría. */

// Códigos de dos letras que se dan por buenos (ISO 3166). Quedan fuera los que en una lista de
// canales suelen ser otra cosa: TV, SD, FM, ID…
const REGIONS = 'AD AE AL AM AO AT AU AZ BA BD BE BG BO BR BY CA CH CL CM CN CO CR CU CY CZ DE DK DO DZ EC EE EG ES ET FI FR GB GE GH GR GT HK HN HR HT HU IE IL IN IQ IR IS IT JO JP KE KR KW KZ LB LK LT LU LV LY MA MD ME MK MT MX MY NG NI NL NO NP NZ PA PE PH PK PL PR PT PY QA RO RS RU SA SE SI SK SN SV SY TH TN TR TW UA US UY UZ VE VN XK ZA'.split(' ');

// Otras formas de escribirlo → código. Los grupos que no son un país llevan nombre propio.
const ALIASES = {
    UK: 'GB', ENG: 'GB', USA: 'US', GER: 'DE', DEU: 'DE', FRA: 'FR', ESP: 'ES', SPA: 'ES', ITA: 'IT', POR: 'PT', PRT: 'PT',
    NED: 'NL', NLD: 'NL', HOL: 'NL', BEL: 'BE', SUI: 'CH', SWI: 'CH', AUT: 'AT', TUR: 'TR', BRA: 'BR', MEX: 'MX', ARG: 'AR',
    COL: 'CO', CHI: 'CL', CHL: 'CL', PER: 'PE', VEN: 'VE', ECU: 'EC', URU: 'UY', URY: 'UY', CAN: 'CA', AUS: 'AU', POL: 'PL',
    ROM: 'RO', ROU: 'RO', RUS: 'RU', UKR: 'UA', GRE: 'GR', GRC: 'GR', SWE: 'SE', NOR: 'NO', DEN: 'DK', DNK: 'DK', FIN: 'FI',
    IND: 'IN', PAK: 'PK', CHN: 'CN', JPN: 'JP', KOR: 'KR', ISR: 'IL', IRN: 'IR', ALB: 'AL', SRB: 'RS', CRO: 'HR', HRV: 'HR',
    BUL: 'BG', BGR: 'BG', HUN: 'HU', CZE: 'CZ', MAR: 'MA', EGY: 'EG',
    // «AR» en estas listas es casi siempre «árabe»; Argentina va como ARG.
    AR: 'ARAB', ARA: 'ARAB', ARAB: 'ARAB',
    LAT: 'LATAM', LATAM: 'LATAM', LATINO: 'LATAM',
    EN: 'EN', KU: 'KURD', KURD: 'KURD', AF: 'AFRICA', AFR: 'AFRICA', EXYU: 'EXYU'
};

const GROUPS = {
    ARAB: { es: 'Árabe', en: 'Arabic' },
    LATAM: { es: 'Latinoamérica', en: 'Latin America' },
    EN: { es: 'Inglés', en: 'English' },
    KURD: { es: 'Kurdo', en: 'Kurdish' },
    AFRICA: { es: 'África', en: 'Africa' },
    EXYU: { es: 'Ex-Yugoslavia', en: 'Ex-Yugoslavia' }
};

const REGION_SET = new Set(REGIONS.concat(['AR']));

// El código va en mayúsculas al principio, separado por una barra, un guion, dos puntos o entre
// corchetes. Con guion o dos puntos hace falta un espacio detrás («ES - La 1», no «ES-TV»).
const PREFIX = [
    /^\s*\|?\s*([A-Z]{2,6})\s*\|\s*/,
    /^\s*\[\s*([A-Z]{2,6})\s*\]\s*[-|:]?\s*/,
    /^\s*\(\s*([A-Z]{2,6})\s*\)\s*[-|:]?\s*/,
    /^\s*([A-Z]{2,6})\s*[-:•★]\s+/
];

function resolve(code) {
    const c = String(code || '').toUpperCase().replace(/[^A-Z]/g, '');
    if (!c) return '';
    if (ALIASES[c]) return ALIASES[c];
    return c.length === 2 && REGION_SET.has(c) && c !== 'AR' ? c : '';
}

// Prefijos con barra que no son un país: calidad, formato, servicio…
const NOT_COUNTRY = new Set('HD SD FHD UHD TV VIP VOD PPV XXX FM AM TOP NEW LIVE HEVC RAW 3D HDR SDR DVR SUB DUB VF VOSE VOSE ADULT'.split(' '));

// Mismo prefijo pero con la barra como único separador («ab| Título»): aquí el código puede estar
// en minúsculas o no estar en la lista. Se quita del título, aunque no se sepa de qué país es.
const PIPE_PREFIX = /^\s*\|?\s*([A-Za-z]{2,3})\s*\|\s*/;

/** { code, rest }: el país del prefijo y el nombre sin él; code '' si no hay país reconocible. */
export function splitCountry(name) {
    const s = String(name || '');
    for (const re of PREFIX) {
        const m = re.exec(s);
        if (!m) continue;
        const code = resolve(m[1]);
        const rest = s.slice(m[0].length).trim();
        // Un nombre que se queda vacío no era un prefijo («ES» a secas).
        if (code && rest) return { code, rest };
    }
    const m = PIPE_PREFIX.exec(s);
    if (m && !NOT_COUNTRY.has(m[1].toUpperCase())) {
        const rest = s.slice(m[0].length).trim();
        if (rest) return { code: resolve(m[1]), rest };
    }
    return { code: '', rest: s.trim() };
}

/** País de un elemento: el de su nombre o, si no lo trae, el de su categoría. */
export function countryOf(name, categoryName) {
    return splitCountry(name).code || splitCountry(categoryName).code || '';
}

/** Título sin el prefijo de país: «FR| La Joconde» → «La Joconde». */
export function stripCountry(name) {
    return splitCountry(name).rest;
}

/** Nombre del país en el idioma de la interfaz: «Francia», «Reino Unido»… */
export function countryName(code, lang) {
    const l = String(lang || 'es').slice(0, 2);
    if (GROUPS[code]) return GROUPS[code][l] || GROUPS[code].en;
    try {
        const name = new Intl.DisplayNames([l], { type: 'region' }).of(code);
        if (name && name !== code) return name.charAt(0).toUpperCase() + name.slice(1);
    } catch (e) { /* sin Intl.DisplayNames: se queda el código */ }
    return code;
}

/**
 * Países de una lista de elementos (ya con `region`), de más a menos títulos:
 * [{ id, count }]. Lo que no trae país cuenta como ''.
 */
export function countCountries(items) {
    const counts = new Map();
    (items || []).forEach((it) => {
        const c = it.region || '';
        counts.set(c, (counts.get(c) || 0) + 1);
    });
    return Array.from(counts.entries()).map(([id, count]) => ({ id, count })).sort((a, b) => b.count - a.count || a.id.localeCompare(b.id));
}

/**
 * Título de un episodio sin lo que el proveedor le repite: el prefijo de país y el nombre de la serie
 * con su año y su numeración. «MU| Beauty in Black (2024) - S01E01 - Make It Rain» → «Make It Rain».
 * Si tras la numeración no queda nada, se conserva lo que había (sin el país).
 */
export function episodeTitle(title) {
    const t = stripCountry(title);
    const m = /^.*?\b(?:S\d{1,3}\s*E\d{1,4}|\d{1,2}x\d{2,3})\b\s*[-–—:.·|]*\s*(.*)$/i.exec(t);
    return m && m[1].trim() ? m[1].trim() : t;
}

/** Limpia los títulos de los episodios de una ficha de serie, en su sitio. */
export function tidyEpisodes(info) {
    if (info && Array.isArray(info.seasons)) {
        info.seasons.forEach((s) => (s.episodes || []).forEach((e) => { if (e && e.title) e.title = episodeTitle(e.title); }));
    }
    return info;
}
